import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { buildWageOrderPDFs } from "./wageOrderGenerator";
import { filingDebtors as getFilingDebtors } from "../utils/debtors";
import { wageOrderPlanPayment } from "../utils/wageOrderPlan";

const currency = (v) => {
  if (v === "" || v === null || v === undefined || Number.isNaN(Number(v))) return "";
  const n = Number(v);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

async function apiJson(path, options = {}) {
  const token = localStorage.getItem("accessToken");
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || payload.error || `Request failed (${response.status})`);
  return payload;
}

export default function WageOrder() {
  const { caseId } = useParams();
  const [loading, setLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [chapter13Plan, setChapter13Plan] = useState(null);
  const [planNotice, setPlanNotice] = useState("");
  const [error, setError] = useState("");
  const [filingPreflight, setFilingPreflight] = useState(null);
  const [filing, setFiling] = useState(null);
  const [filingError, setFilingError] = useState("");
  const [filingAcknowledged, setFilingAcknowledged] = useState(false);
  const [redactionAcknowledged, setRedactionAcknowledged] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [resolvingDocketNumber, setResolvingDocketNumber] = useState("");
  const [resolvingOrderId, setResolvingOrderId] = useState("");
  const [generatedDocuments, setGeneratedDocuments] = useState(null);

  // Form state
  const [debtorId, setDebtorId] = useState("");
  const [employerId, setEmployerId] = useState("");
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState("monthly"); // weekly|biweekly|semimonthly|monthly|custom
  const [customPeriodText, setCustomPeriodText] = useState("");

  // Load the case
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      setPlanNotice("");
      try {
        const token = localStorage.getItem("accessToken");
        const headers = { Authorization: `Bearer ${token}` };
        const [resp, planResp, filingResp] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/overview`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/chapter-13-plan/`, { headers }),
          fetch(`${API_BASE}/firm/cases/${caseId}/cmecf-wage-order/preflight/`, { headers }),
        ]);
        if (!resp.ok) throw new Error(`Failed to load case ${caseId}: ${resp.status}`);
        const data = await resp.json();
        const plan = planResp.ok ? await planResp.json() : null;
        const preflight = filingResp.ok ? await filingResp.json() : null;
        if (!cancelled) {
          setCaseData(data);
          setChapter13Plan(plan);
          setFilingPreflight(preflight);
          const active = (preflight?.recent_filings || []).find((item) =>
            ["queued", "in_progress", "review_required"].includes(item.status)
          );
          setFiling(active || null);
          if (!filingResp.ok) {
            const payload = await filingResp.json().catch(() => ({}));
            setFilingError(payload.detail || "The CM/ECF wage-order review could not be loaded.");
          }
          if (!planResp.ok) {
            setPlanNotice(
              planResp.status === 404
                ? "No saved Chapter 13 plan was found. Enter the wage-order payment manually."
                : "The Chapter 13 plan could not be loaded. Enter the payment manually or reload the page."
            );
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (caseId) load();
    return () => { cancelled = true; };
  }, [caseId]);

  // Filing debtors list
  const filingDebtors = useMemo(() => {
    return getFilingDebtors(caseData?.debtors);
  }, [caseData]);

  // Employers for selected debtor (supports multiple shapes we use elsewhere)
  const debtorEmployers = useMemo(() => {
    if (!debtorId || !caseData?.debtors) return [];
    const d = caseData.debtors.find((x) => String(x.id) === String(debtorId));
    if (!d) return [];

    const list = [];

    // 1) Preferred: Overview API shape — d.employments[]
    if (Array.isArray(d.employments)) {
      d.employments.forEach((empRec) => {
        if (!empRec) return;
        const name = empRec.employer || '';
        const city = empRec.employer_city || '';
        const state = empRec.employer_state || '';
        const label = [name, city && state ? `(${city}, ${state})` : ''].filter(Boolean).join(' ');
        list.push({
          id: empRec.id ?? `${name}-${city}-${state}`,
          employer_name: name,
          street1: empRec.employer_street || '',
          street2: empRec.employer_street2 || '',
          city,
          state,
          zip: empRec.employer_zip || '',
          phone: empRec.employer_phone || '',
          label,
        });
      });
    }

    // 2) Backward compatibility: older shapes seen elsewhere in app
    const pushIf = (emp) => {
      if (!emp) return;
      const name = emp.employer_name || emp.employer || '';
      const city = emp.city || emp.employer_city || '';
      const state = emp.state || emp.employer_state || '';
      const label = [name, city && state ? `(${city}, ${state})` : ''].filter(Boolean).join(' ');
      list.push({
        id: emp.id ?? `${name}-${city}-${state}`,
        employer_name: name,
        street1: emp.street1 || emp.address || emp.employer_street || '',
        street2: emp.street2 || emp.employer_street2 || '',
        city,
        state,
        zip: emp.zip || emp.employer_zip || '',
        phone: emp.phone || emp.employer_phone || '',
        label,
      });
    };
    if (Array.isArray(d.employment_history)) d.employment_history.forEach(pushIf);
    if (d.current_employer) pushIf(d.current_employer);
    if (d.prior_employer) pushIf(d.prior_employer);
    if (Array.isArray(d.employers)) d.employers.forEach(pushIf);

    // De‑dupe by (name, city, state)
    const seen = new Set();
    return list.filter((e) => {
      const key = `${e.employer_name}|${e.city}|${e.state}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [caseData, debtorId]);

  const employerSelected = useMemo(() => {
    if (!employerId) return null;
    return debtorEmployers.find((e) => String(e.id) === String(employerId)) || null;
  }, [debtorEmployers, employerId]);

  const selectedDebtor = useMemo(() => {
    if (!debtorId) return null;
    return filingDebtors.find((d) => String(d.id) === String(debtorId)) || null;
  }, [filingDebtors, debtorId]);

  const selectedPlanPayment = useMemo(() => (
    wageOrderPlanPayment(chapter13Plan, caseData?.debtors, debtorId)
  ), [chapter13Plan, caseData, debtorId]);

  const latestSelectedDebtorFiling = useMemo(() => (
    (filingPreflight?.recent_filings || []).find(
      (item) => String(item.debtor_id) === String(debtorId)
    ) || null
  ), [filingPreflight, debtorId]);

  const resumableFiling = latestSelectedDebtorFiling?.can_resume
    ? latestSelectedDebtorFiling
    : null;
  const filingActive = filing && ["queued", "in_progress", "review_required"].includes(filing.status);
  const displayedFiling = filingActive
    ? filing
    : String(filing?.debtor_id || "") === String(debtorId || "")
      ? filing
      : latestSelectedDebtorFiling;

  useEffect(() => {
    if (!filing?.id || !["queued", "in_progress"].includes(filing.status)) return undefined;
    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const current = await apiJson(`/firm/cmecf-wage-orders/${filing.id}/`);
        if (!cancelled) {
          setFiling(current);
          setFilingPreflight((previous) => previous ? {
            ...previous,
            recent_filings: [
              current,
              ...(previous.recent_filings || []).filter((item) => item.id !== current.id),
            ],
          } : previous);
        }
      } catch (requestError) {
        if (!cancelled) setFilingError(requestError.message || "Could not check the filing status.");
      } finally {
        polling = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [filing?.id, filing?.status]);

  const selectDebtor = (nextDebtorId) => {
    setDebtorId(nextDebtorId);
    setEmployerId("");
    setCustomPeriodText("");
    const planPayment = wageOrderPlanPayment(
      chapter13Plan,
      caseData?.debtors,
      nextDebtorId,
    );
    setAmount(planPayment?.amount || "");
    setPeriod(planPayment?.period || "monthly");
    setFilingError("");
    setFilingAcknowledged(false);
    setRedactionAcknowledged(false);
    setConfirmationText("");
  };

  const reapplyPlanPayment = () => {
    if (!selectedPlanPayment) return;
    setAmount(selectedPlanPayment.amount || "");
    setPeriod(selectedPlanPayment.period || "monthly");
    setCustomPeriodText("");
  };

  const canGenerate = !!(selectedDebtor && employerSelected && amount && (period !== "custom" || customPeriodText.trim().length));

  const generatedDocumentUrls = useMemo(() => {
    if (!generatedDocuments) return null;
    return {
      motion: URL.createObjectURL(generatedDocuments.motion.blob),
      order: URL.createObjectURL(generatedDocuments.order.blob),
    };
  }, [generatedDocuments]);

  useEffect(() => {
    setGeneratedDocuments(null);
  }, [caseData, debtorId, employerId, amount, period, customPeriodText]);

  useEffect(() => () => {
    if (!generatedDocumentUrls) return;
    URL.revokeObjectURL(generatedDocumentUrls.motion);
    URL.revokeObjectURL(generatedDocumentUrls.order);
  }, [generatedDocumentUrls]);

  const onGenerate = async () => {
    if (!canGenerate) return;
    try {
      setLoading(true);
      const documents = await buildWageOrderPDFs({
        caseData,
        debtor: selectedDebtor,
        employer: employerSelected,
        amount: Number(amount),
        period,
        customPeriodText: period === "custom" ? customPeriodText.trim() : "",
      });
      setGeneratedDocuments(documents);
    } catch (e) {
      console.error(e);
      alert("Failed to generate the wage-order PDFs.");
    } finally {
      setLoading(false);
    }
  };

  const canFile = Boolean(
    (resumableFiling ? debtorId : canGenerate)
    && filingPreflight?.ready
    && !filingActive
    && filingAcknowledged
    && redactionAcknowledged
    && confirmationText.trim().toUpperCase() === "FILE"
  );

  const onFile = async () => {
    if (!canFile) return;
    setLoading(true);
    setFilingError("");
    try {
      const formData = new FormData();
      formData.append("debtor_id", debtorId);
      formData.append("filing_acknowledged", "true");
      formData.append("redaction_acknowledged", "true");
      formData.append("confirmation_text", "FILE");
      if (resumableFiling) {
        formData.append("resume_filing_id", resumableFiling.id);
      } else {
        const documents = await buildWageOrderPDFs({
          caseData,
          debtor: selectedDebtor,
          employer: employerSelected,
          amount: Number(amount),
          period,
          customPeriodText: period === "custom" ? customPeriodText.trim() : "",
        });
        formData.append("motion_pdf", documents.motion.blob, documents.motion.filename);
        formData.append("order_pdf", documents.order.blob, documents.order.filename);
      }
      const current = await apiJson(
        `/firm/cases/${caseId}/cmecf-wage-order/start/`,
        { method: "POST", body: formData }
      );
      setFiling(current);
      setFilingPreflight((previous) => previous ? {
        ...previous,
        recent_filings: [
          current,
          ...(previous.recent_filings || []).filter((item) => item.id !== current.id),
        ],
      } : previous);
      setFilingAcknowledged(false);
      setRedactionAcknowledged(false);
      setConfirmationText("");
    } catch (requestError) {
      setFilingError(requestError.message || "The wage-order filing could not be started.");
    } finally {
      setLoading(false);
    }
  };

  const resolveUncertain = async (outcome) => {
    if (!filing?.id) return;
    setLoading(true);
    setFilingError("");
    try {
      const current = await apiJson(`/firm/cmecf-wage-orders/${filing.id}/resolve/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          outcome,
          motion_docket_number: resolvingDocketNumber,
          order_id: resolvingOrderId,
        }),
      });
      setFiling(current);
      setFilingPreflight((previous) => previous ? {
        ...previous,
        recent_filings: [
          current,
          ...(previous.recent_filings || []).filter((item) => item.id !== current.id),
        ],
      } : previous);
    } catch (requestError) {
      setFilingError(requestError.message || "The court-check result could not be saved.");
    } finally {
      setLoading(false);
    }
  };

  const cancelQueued = async () => {
    if (!filing?.id) return;
    setLoading(true);
    setFilingError("");
    try {
      const current = await apiJson(`/firm/cmecf-wage-orders/${filing.id}/cancel-queued/`, {
        method: "POST",
      });
      setFiling(current);
    } catch (requestError) {
      setFilingError(requestError.message || "The queued request could not be cancelled.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="space-y-8 mt-12 max-w-3xl mx-auto">
      <header className="flex items-center justify-between bg-gray-100 p-4 rounded-md">
        <h2 className="text-2xl font-semibold">Wage Order</h2>
      </header>
      <p className="text-gray-600">Generate court wage order forms. Choose the filing debtor and employer, confirm the payment amount and period from the Chapter 13 plan, then click “Generate PDFs.” Only one debtor needs to be listed on the form in a joint case.</p>
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {loading && (
          <div className="rounded-md bg-blue-50 p-3 text-blue-800">Working…</div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-red-800">{error}</div>
        )}
        {planNotice && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
            {planNotice}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm text-gray-700 mb-1">Debtor</span>
            <select
              className="w-full rounded-md bg-white border border-gray-300 text-gray-700 p-2"
              value={debtorId}
              onChange={(e) => selectDebtor(e.target.value)}
            >
              <option value="">Select a filing debtor…</option>
              {filingDebtors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.first_name}{d.second_name ? ` ${d.second_name}` : ""} {d.last_name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm text-gray-700 mb-1">Employer</span>
            <select
              className="w-full rounded-md bg-white border border-gray-300 text-gray-700 p-2 disabled:opacity-60"
              value={employerId}
              onChange={(e) => setEmployerId(e.target.value)}
              disabled={!debtorId}
            >
              <option value="">{debtorId ? "Select employer…" : "Choose debtor first"}</option>
              {debtorEmployers.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.employer_name} {emp.city && emp.state ? `(${emp.city}, ${emp.state})` : ""}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm text-gray-700 mb-1">Payment Amount</span>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-400">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full rounded-md bg-white border border-gray-300 text-gray-700 p-2 pl-7"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Formatted: <span className="font-medium">{currency(amount) || "—"}</span></p>
          </label>

          <label className="block">
            <span className="block text-sm text-gray-700 mb-1">Payment Period</span>
            <select
              className="w-full rounded-md bg-white border border-gray-300 text-gray-700 p-2"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 Weeks</option>
              <option value="semimonthly">Twice per Month</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom…</option>
            </select>
          </label>
        </div>

        {selectedPlanPayment && (
          <div className={`rounded-md border p-4 ${
            selectedPlanPayment.source === "payroll"
              ? "border-green-200 bg-green-50 text-green-900"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold">
                  {selectedPlanPayment.source === "payroll"
                    ? `Debtor ${selectedPlanPayment.debtorNumber} is marked Payroll in the plan.`
                    : `Debtor ${selectedPlanPayment.debtorNumber} is not marked Payroll in the plan.`}
                </div>
                <p className="mt-1 text-sm">
                  The saved current-plan amount and period were placed in the form. You may change them before generating.
                </p>
              </div>
              <button
                type="button"
                onClick={reapplyPlanPayment}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50"
              >
                Restore plan values
              </button>
            </div>
          </div>
        )}

        {period === "custom" && (
          <div className="mt-4">
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">Describe the payment schedule</span>
              <input
                className="w-full rounded-md bg-white border border-gray-300 text-gray-700 p-2"
                value={customPeriodText}
                onChange={(e) => setCustomPeriodText(e.target.value)}
                placeholder="e.g., Every other Friday beginning next pay period"
              />
            </label>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-700">
          {caseData?.case_number ? (
            <div>
              <span className="text-gray-500">Case:</span> <span className="font-medium">{caseData.case_number}</span>
              {selectedDebtor && (
                <>
                  {" • "}<span className="text-gray-500">Debtor:</span> <span className="font-medium">
                    {selectedDebtor.first_name}{selectedDebtor.second_name ? ` ${selectedDebtor.second_name}` : ""} {selectedDebtor.last_name}
                  </span>
                </>
              )}
              {employerSelected && (
                <>
                  {" • "}<span className="text-gray-500">Employer:</span> <span className="font-medium">{employerSelected.employer_name}</span>
                </>
              )}
            </div>
          ) : (
            <span className="text-gray-500">Loading case…</span>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onGenerate}
            disabled={!canGenerate || loading}
            className={`px-4 py-2 bg-blue-600 text-white rounded ${canGenerate ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'} transition`}
          >
            Generate PDFs
          </button>
          <p className="text-sm text-gray-500">Generate both documents, then open each PDF separately.</p>
        </div>

        {generatedDocuments && (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <div className="font-semibold text-green-900">Motion and order are ready.</div>
            <p className="mt-1 text-sm text-green-800">
              Each document opens in a new tab, where you can review, print, or download it.
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <a
                href={generatedDocumentUrls.motion}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
              >
                Open Motion PDF
              </a>
              <a
                href={generatedDocumentUrls.order}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
              >
                Open Order PDF
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-red-200 bg-white p-6 shadow-md space-y-5">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">File automatically with CM/ECF</h3>
          <p className="mt-1 text-sm text-gray-600">
            This files the motion for only the debtor selected above, captures its new docket number,
            and then uploads the proposed order through Single Order Upload as a Wage Order.
          </p>
        </div>

        {filingError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
            {filingError}
          </div>
        )}

        {(filingPreflight?.reasons || []).length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-semibold">Automatic filing is not ready:</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {filingPreflight.reasons.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          </div>
        )}

        {resumableFiling && !filingActive && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
            <div className="font-semibold">Resume the reviewed filing for {resumableFiling.debtor_name}</div>
            <p className="mt-1">
              The saved PDFs will be reused. A completed motion will not be filed twice; only the unfinished court step will run.
            </p>
          </div>
        )}

        {!filingActive && (
          <div className="space-y-3">
            <label className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-950">
              <input
                type="checkbox"
                checked={filingAcknowledged}
                onChange={(event) => setFilingAcknowledged(event.target.checked)}
                className="mt-1"
              />
              I reviewed the selected debtor, employer, payment terms, motion, and proposed order. I understand this will make a live CM/ECF filing and a live Single Order Upload.
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={redactionAcknowledged}
                onChange={(event) => setRedactionAcknowledged(event.target.checked)}
                className="mt-1"
              />
              I confirm that both PDFs comply with the court&apos;s redaction requirements.
            </label>
            <label className="block text-sm font-medium text-gray-900">
              Type <strong>FILE</strong> to enable the filing button.
              <input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.target.value)}
                className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              onClick={onFile}
              disabled={!canFile || loading}
              className="rounded-md bg-red-700 px-5 py-3 font-bold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading
                ? "Preparing…"
                : resumableFiling
                  ? "Resume wage-order filing"
                  : "Generate PDFs and file wage order"}
            </button>
          </div>
        )}

        {displayedFiling && (
          <div className={`rounded-lg border p-4 ${
            displayedFiling.status === "succeeded"
              ? "border-green-200 bg-green-50"
              : displayedFiling.status === "review_required"
                ? "border-red-300 bg-red-50"
                : ["failed", "partial"].includes(displayedFiling.status)
                  ? "border-amber-300 bg-amber-50"
                  : "border-indigo-200 bg-indigo-50"
          }`}>
            <div className="font-bold text-gray-900">{displayedFiling.status_display}</div>
            <p className="mt-1 text-sm text-gray-700">{displayedFiling.status_message}</p>
            <p className="mt-1 text-sm text-gray-700">Debtor: <strong>{displayedFiling.debtor_name}</strong></p>
            {displayedFiling.motion_docket_number && (
              <p className="mt-1 text-sm font-semibold text-gray-900">
                Motion docket: {displayedFiling.motion_docket_number}
              </p>
            )}
            {displayedFiling.order_id && (
              <p className="mt-1 text-sm font-semibold text-gray-900">
                Single Order Upload ID: {displayedFiling.order_id}
              </p>
            )}
            {displayedFiling.error_message && (
              <p className="mt-2 text-sm font-medium text-red-800">{displayedFiling.error_message}</p>
            )}
            <div className="mt-3 space-y-2">
              {(displayedFiling.steps || []).map((step) => (
                <div key={step.key} className="flex flex-wrap justify-between gap-2 rounded-md bg-white/80 px-3 py-2 text-sm">
                  <span className="font-medium text-gray-800">{step.label}</span>
                  <span className={step.status === "succeeded" ? "font-semibold text-green-700" : step.status === "review_required" ? "font-semibold text-red-700" : "text-gray-600"}>
                    {String(step.status || "pending").replaceAll("_", " ")}
                    {step.docket_number ? ` · docket ${step.docket_number}` : ""}
                    {step.order_id ? ` · Order ID ${step.order_id}` : ""}
                  </span>
                </div>
              ))}
            </div>

            {displayedFiling.status === "review_required" && (
              <div className="mt-4 rounded-md border border-red-300 bg-white p-4">
                <p className="text-sm font-bold text-red-900">
                  Check the live docket or Single Order Upload before answering. Do not retry first.
                </p>
                {displayedFiling.current_step === "motion" && (
                  <label className="mt-3 block text-sm text-gray-700">
                    Wage-order motion docket number
                    <input
                      value={resolvingDocketNumber}
                      onChange={(event) => setResolvingDocketNumber(event.target.value)}
                      className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                    />
                  </label>
                )}
                {displayedFiling.current_step === "order_upload" && (
                  <label className="mt-3 block text-sm text-gray-700">
                    Order ID, if shown
                    <input
                      value={resolvingOrderId}
                      onChange={(event) => setResolvingOrderId(event.target.value)}
                      className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 p-2"
                    />
                  </label>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => resolveUncertain("completed")} disabled={loading} className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    I checked — this step completed
                  </button>
                  <button type="button" onClick={() => resolveUncertain("not_completed")} disabled={loading} className="rounded-md border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50">
                    I checked — it did not complete
                  </button>
                </div>
              </div>
            )}

            {displayedFiling.status === "queued" && displayedFiling.can_cancel_queued && (
              <button type="button" onClick={cancelQueued} disabled={loading} className="mt-4 rounded-md border border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900">
                Cancel stalled queue request
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
